import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RefundVouchersService {
  private _baseUrl: string = environment.apiUrl;
  private _controllerName: string = "RefundVouchers";

  constructor(private _http: HttpClient) { }

  // CRUD operations
  insert(payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}`, payload)
      .pipe(catchError(this.handleErrors));
  }

  update(id: number, payload: any): Observable<any> {
    return this._http.put(`${this._baseUrl}/${this._controllerName}/${id}`, payload)
      .pipe(catchError(this.handleErrors));
  }

  delete(id: number): Observable<any> {
    return this._http.delete(`${this._baseUrl}/${this._controllerName}/${id}`)
      .pipe(catchError(this.handleErrors));
  }

  getAll(
    status?: string,
    refundType?: string,
    fromDate?: Date,
    toDate?: Date,
    dateType?: 'Creation' | 'Payment'
  ): Observable<any> {
    let queryParams = '';

    if (status) queryParams += `status=${status}&`;
    if (refundType) queryParams += `refundType=${refundType}&`;
    if (fromDate) queryParams += `fromDate=${fromDate.toISOString()}&`;
    if (toDate) queryParams += `toDate=${toDate.toISOString()}&`;
    if (dateType) queryParams += `dateType=${dateType}&`;

    // Remove trailing & if exists
    queryParams = queryParams ? `?${queryParams.slice(0, -1)}` : '';

    return this._http.get(`${this._baseUrl}/${this._controllerName}${queryParams}`)
      .pipe(catchError(this.handleErrors));
  }

  getById(id: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/${id}`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Descarga el PDF del comprobante de devolución
   * @param id ID del comprobante
   */
  downloadPdf(id: number): Observable<Blob> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/${id}/pdf`, { responseType: 'blob' })
      .pipe(catchError(this.handleErrors));
  }

  // Specialized creation methods
  createFromExcess(payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/from-excess`, payload)
      .pipe(catchError(this.handleErrors));
  }

  createFromPlan(payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/from-plan`, payload)
      .pipe(catchError(this.handleErrors));
  }

  createFromService(payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/from-service`, payload)
      .pipe(catchError(this.handleErrors));
  }

  // Approval and processing
  processApproval(id: number, payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/${id}/approve`, payload)
      .pipe(catchError(this.handleErrors));
  }

  processRefund(id: number, payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/${id}/process`, payload)
      .pipe(catchError(this.handleErrors));
  }

  cancelRefund(id: number, payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/${id}/cancel`, payload)
      .pipe(catchError(this.handleErrors));
  }

  reconcileRefund(id: number, payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/${id}/reconcile`, payload)
      .pipe(catchError(this.handleErrors));
  }

  // Validation methods
  validateExcessRefund(excessPaymentId: number, refundAmount: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/validate-excess/${excessPaymentId}/${refundAmount}`)
      .pipe(catchError(this.handleErrors));
  }

  getMaxRefundableAmount(enrollmentId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/max-refundable/${enrollmentId}`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Obtener información completa de límites de devolución para un plan de pagos.
   * Incluye: TotalAmount, MaxRefundable (80%), MaxRefundableWithRetractRight (100%),
   * TotalPaid, AlreadyRefunded, AvailableToRefund, AvailableToRefundWithRetractRight
   */
  getMaxRefundableFromPlan(enrollmentId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/max-refundable-from-plan/${enrollmentId}`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Obtener información completa de límites de devolución para un plan de pagos corporativo.
   * La validación del 80% o 100% (derecho al retracto) se hace sobre el Enrollment individual del participante.
   */
  getMaxRefundableFromCorporatePlan(corporateEnrollmentId: number, payerType: string, participantId?: number): Observable<any> {
    let url = `${this._baseUrl}/${this._controllerName}/max-refundable-from-corporate-plan/${corporateEnrollmentId}?payerType=${payerType}`;
    if (participantId) {
      url += `&participantId=${participantId}`;
    }
    return this._http.get(url).pipe(catchError(this.handleErrors));
  }

  getAvailableExcessAmount(excessPaymentId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/available-excess/${excessPaymentId}`)
      .pipe(catchError(this.handleErrors));
  }

  getAvailableExcesses(): Observable<any> {
    return this._http.get(`${this._baseUrl}/ExcessPayments/available-for-refund`)
      .pipe(catchError(this.handleErrors));
  }

  getAvailableExcessesByParticipantAndProject(participantId: number, projectId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/ExcessPayments/participant/${participantId}/project/${projectId}/available`)
      .pipe(catchError(this.handleErrors));
  }

  getMaxServiceRefundableAmount(participantId: number, projectId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/max-service-refundable/${participantId}/${projectId}`)
      .pipe(catchError(this.handleErrors));
  }

  // Filter methods
  getByParticipantId(participantId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/participant/${participantId}`)
      .pipe(catchError(this.handleErrors));
  }

  getByProjectId(projectId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/project/${projectId}`)
      .pipe(catchError(this.handleErrors));
  }

  getByEnrollmentId(enrollmentId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/enrollment/${enrollmentId}`)
      .pipe(catchError(this.handleErrors));
  }

  getByBankId(bankId: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/bank/${bankId}`)
      .pipe(catchError(this.handleErrors));
  }

  getByStatus(status: string): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/status/${status}`)
      .pipe(catchError(this.handleErrors));
  }

  getPendingApprovals(): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/pending-approvals`)
      .pipe(catchError(this.handleErrors));
  }

  getApprovedPending(): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/approved-pending`)
      .pipe(catchError(this.handleErrors));
  }

  getUnreconciled(): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/unreconciled`)
      .pipe(catchError(this.handleErrors));
  }

  // Summary and reporting
  getRefundSummary(fromDate: Date, toDate: Date, bankId?: number): Observable<any> {
    let queryParams = `?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`;
    if (bankId) queryParams += `&bankId=${bankId}`;
    
    return this._http.get(`${this._baseUrl}/${this._controllerName}/summary${queryParams}`)
      .pipe(catchError(this.handleErrors));
  }

  getAuditTrail(id: number): Observable<any> {
    return this._http.get(`${this._baseUrl}/${this._controllerName}/${id}/audit-trail`)
      .pipe(catchError(this.handleErrors));
  }

  // CORPORATE REFUND METHODS

  // Corporate creation methods
  createFromCorporateExcess(payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/create-from-corporate-excess`, payload)
      .pipe(catchError(this.handleErrors));
  }

  createFromCorporatePlan(payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/create-from-corporate-plan`, payload)
      .pipe(catchError(this.handleErrors));
  }

  createFromCorporateService(payload: any): Observable<any> {
    return this._http.post(`${this._baseUrl}/${this._controllerName}/create-from-corporate-service`, payload)
      .pipe(catchError(this.handleErrors));
  }

  // Corporate data methods
  getAvailableCorporateExcesses(corporateEnrollmentId: number, payerType: string, participantId?: number): Observable<any> {
    let url = `${this._baseUrl}/${this._controllerName}/available-corporate-excesses?corporateEnrollmentId=${corporateEnrollmentId}&payerType=${payerType}`;
    if (participantId) {
      url += `&participantId=${participantId}`;
    }
    return this._http.get(url).pipe(catchError(this.handleErrors));
  }

  getAvailableCorporateInstallments(corporateEnrollmentId: number, payerType: string, participantId?: number): Observable<any> {
    let url = `${this._baseUrl}/${this._controllerName}/available-corporate-installments?corporateEnrollmentId=${corporateEnrollmentId}&payerType=${payerType}`;
    if (participantId) {
      url += `&participantId=${participantId}`;
    }
    return this._http.get(url).pipe(catchError(this.handleErrors));
  }

  getAvailableCorporateReceipts(corporateEnrollmentId: number, payerType: string, participantId?: number): Observable<any> {
    let url = `${this._baseUrl}/${this._controllerName}/available-corporate-receipts?corporateEnrollmentId=${corporateEnrollmentId}&payerType=${payerType}`;
    if (participantId) {
      url += `&participantId=${participantId}`;
    }
    return this._http.get(url).pipe(catchError(this.handleErrors));
  }

  getMaxCorporateRefundableAmount(corporateEnrollmentId: number, payerType: string, participantId?: number): Observable<any> {
    let url = `${this._baseUrl}/${this._controllerName}/max-corporate-refundable?corporateEnrollmentId=${corporateEnrollmentId}&payerType=${payerType}`;
    if (participantId) {
      url += `&participantId=${participantId}`;
    }
    return this._http.get(url).pipe(catchError(this.handleErrors));
  }

  private handleErrors(error: HttpErrorResponse) {
    let errorMessage = 'Error desconocido';
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = error.error?.message || error.message || `Código: ${error.status}, Mensaje: ${error.statusText}`;
    }
    return throwError(() => new Error(errorMessage));
  }
}